import { FC, useCallback, useState } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@new/common/ui/Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@new/common/ui/Popover/Popover';
import { Button } from '@new/common/ui/Button/Button';

const DATE_DISPLAY_FORMAT = 'MMM d, yyyy';

interface DateRangeFilterProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
}

export const DateRangeFilter: FC<DateRangeFilterProps> = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}) => {
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const fromDate = dateFrom ? new Date(dateFrom) : undefined;
  const toDate = dateTo ? new Date(dateTo) : undefined;
  const hasDateFilter = dateFrom || dateTo;

  const onFromSelect = useCallback(
    (date: Date | undefined) => {
      onDateFromChange(date ? format(date, 'yyyy-MM-dd') : '');
      setFromOpen(false);
    },
    [onDateFromChange]
  );

  const onToSelect = useCallback(
    (date: Date | undefined) => {
      onDateToChange(date ? format(date, 'yyyy-MM-dd') : '');
      setToOpen(false);
    },
    [onDateToChange]
  );

  const onClear = () => {
    onDateFromChange('');
    onDateToChange('');
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={fromOpen} onOpenChange={setFromOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 cursor-pointer rounded-md! border border-(--neutral-white-NW800) text-(--neutral-black-NB500) hover:text-(--neutral-black-NB800)"
            >
              <CalendarIcon size={12} />
              <span className="[font-size:var(--fs-l5)]">
                {fromDate ? format(fromDate, DATE_DISPLAY_FORMAT) : 'From'}
              </span>
            </Button>
          }
        />
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={fromDate}
            onSelect={onFromSelect}
            disabled={toDate ? (date) => date > toDate : undefined}
          />
        </PopoverContent>
      </Popover>

      <span className="[font-size:var(--fs-l5)] text-(--neutral-black-NB600)">—</span>

      <Popover open={toOpen} onOpenChange={setToOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 cursor-pointer rounded-md! border border-(--neutral-white-NW800) text-(--neutral-black-NB500) hover:text-(--neutral-black-NB800)"
            >
              <CalendarIcon size={14} />
              <span className="[font-size:var(--fs-l5)]">
                {toDate ? format(toDate, DATE_DISPLAY_FORMAT) : 'To'}
              </span>
            </Button>
          }
        />
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={toDate}
            onSelect={onToSelect}
            disabled={fromDate ? (date) => date < fromDate : undefined}
          />
        </PopoverContent>
      </Popover>

      {hasDateFilter && (
        <button
          onClick={onClear}
          className="p-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <X size={14} className="text-(--neutral-black-NB600) rounded-full!" />
        </button>
      )}
    </div>
  );
};
